import _ from "lodash";
import { call, put } from "redux-saga/effects";
import { v4 as uuid4 } from "uuid";

import actions from "@/actions";
import { Api, ConfigurationManager } from "@/utils";

const convertApiLightGroups = (groups) =>
    groups.map(({ color, displayID, floorSpaceID, groupID, groupName }) => ({
        color,
        displayID,
        floorSpaceID,
        groupID,
        groupName,
    }));

const convertApiPath = ({ swapXY, floorSpaceLength }, path) => {
    if (swapXY) {
        return path.map(({ x, y }) => ({
            lat: x,
            lng: -y + floorSpaceLength,
        }));
    }
    return path.map(({ x, y }) => ({ lat: y, lng: x }));
};

const convertApiRegion = (region, group, { swapXY, floorSpaceLength } = {}) =>
    region.map(({ path, zIndex }) => {
        const vertices = convertApiPath({ swapXY, floorSpaceLength }, path);
        return {
            group,
            id: uuid4(),
            vertices: [...vertices, vertices[0]],
            zIndex,
        };
    });

/**
 * Loads light groups and their region geometries from the API for the current floor space.
 *
 * @param root0 - The configuration options.
 * @param root0.swapXY - Whether to swap X and Y coordinates.
 * @param root0.floorSpaceLength - The floor space length for coordinate conversion.
 * @returns The loaded light groups and geometries.
 * @yields Calls the API and dispatches light group data.
 */
function* loadLightGroups({ swapXY = false, floorSpaceLength = 0 } = {}) {
    try {
        const { floorSpaceId } = ConfigurationManager.values;

        let { floorGroups } = yield call(
            Api.get,
            `v2/control-groups/with-luminaire-summary-by-floor-space-id-in-meters/${floorSpaceId}`,
        );

        // The backend returns a fake default group if one doesn't exist, but it ONLY does this if there are ungrouped
        // luminaires. This is not compatible with how we handle groups here so we can just safely ignore it.
        floorGroups = floorGroups.filter(({ groupID }) => groupID !== 0);

        const lightGroups = convertApiLightGroups(floorGroups);
        const lightGroupsById = _.groupBy(lightGroups, "groupID");

        const groupGeometries = [];

        for (const group of floorGroups) {
            groupGeometries.push(
                ...convertApiRegion(
                    group.region,
                    lightGroupsById[group.groupID][0],
                    { swapXY, floorSpaceLength },
                ),
            );
        }

        yield put(actions.map.general.setLightGroups(lightGroups));

        yield put(
            actions.map.values.global.setGroupGeometries(groupGeometries),
        );

        return lightGroups;
    } catch (err) {
        // eslint-disable-next-line no-console
        console.log("error loading light groups: ", err);
        return [];
    }
}

export default loadLightGroups;
